import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import CinematicLoader from '@src/components/CinematicLoader';

type Props = {
  onLogin: (token: string) => void;
};

const COGNITO_AUTH_URL = import.meta.env.VITE_COGNITO_AUTH_URL || '';
const COGNITO_CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID || '';
const COGNITO_REDIRECT_URI = import.meta.env.VITE_COGNITO_REDIRECT_URI || `${window.location.origin}/login`;
const COGNITO_IDP_URL = COGNITO_AUTH_URL
  ? `https://cognito-idp.${new URL(COGNITO_AUTH_URL).hostname.split('.')[2]}.amazonaws.com/`
  : '';

type PasswordReqs = {
  minLength: boolean;
  hasLowercase: boolean;
  hasUppercase: boolean;
  hasNumber: boolean;
};

function validatePassword(pw: string): PasswordReqs {
  return {
    minLength: pw.length >= 8,
    hasLowercase: /[a-z]/.test(pw),
    hasUppercase: /[A-Z]/.test(pw),
    hasNumber: /\d/.test(pw),
  };
}

function isPasswordValid(reqs: PasswordReqs): boolean {
  return reqs.minLength && reqs.hasLowercase && reqs.hasUppercase && reqs.hasNumber;
}

function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return btoa(String.fromCharCode(...array))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

const requirementsList = [
  { key: 'minLength' as const, label: 'At least 8 characters' },
  { key: 'hasLowercase' as const, label: 'One lowercase letter' },
  { key: 'hasUppercase' as const, label: 'One uppercase letter' },
  { key: 'hasNumber' as const, label: 'One number' },
];

const RegisterPage = ({ onLogin }: Props) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [verificationSent, setVerificationSent] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [reqs, setReqs] = useState<PasswordReqs>({
    minLength: false,
    hasLowercase: false,
    hasUppercase: false,
    hasNumber: false,
  });

  useEffect(() => {
    if (password) setReqs(validatePassword(password));
  }, [password]);

  const handleGoogleSignUp = async () => {
    if (!COGNITO_AUTH_URL || !COGNITO_CLIENT_ID) return;
    setLoading(true);
    const verifier = generateCodeVerifier();
    const challenge = await generateCodeChallenge(verifier);
    sessionStorage.setItem('pkce_verifier', verifier);
    window.location.href =
      `${COGNITO_AUTH_URL}/oauth2/authorize?client_id=${COGNITO_CLIENT_ID}` +
      `&response_type=code&scope=openid+email+profile` +
      `&redirect_uri=${encodeURIComponent(COGNITO_REDIRECT_URI)}` +
      `&code_challenge=${challenge}&code_challenge_method=S256` +
      `&identity_provider=Google`;
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (!isPasswordValid(reqs)) {
      setError('Password does not meet requirements');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const res = await fetch(COGNITO_IDP_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-amz-json-1.1',
          'X-Amz-Target': 'AWSCognitoIdentityProviderService.SignUp',
        },
        body: JSON.stringify({
          ClientId: COGNITO_CLIENT_ID,
          Username: email,
          Password: password,
          UserAttributes: [
            { Name: 'email', Value: email },
            { Name: 'given_name', Value: firstName },
            { Name: 'family_name', Value: lastName },
          ],
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.message || 'Sign up failed');
      }

      setVerificationSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign up failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const res = await fetch(COGNITO_IDP_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-amz-json-1.1',
          'X-Amz-Target': 'AWSCognitoIdentityProviderService.ConfirmSignUp',
        },
        body: JSON.stringify({
          ClientId: COGNITO_CLIENT_ID,
          Username: email,
          ConfirmationCode: verificationCode,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.message || 'Verification failed');
      }

      const tokenParams = new URLSearchParams({
        grant_type: 'password',
        client_id: COGNITO_CLIENT_ID,
        username: email,
        password: password,
        scope: 'openid email profile',
      });

      const tokenRes = await fetch(`${COGNITO_AUTH_URL}/oauth2/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: tokenParams.toString(),
      });

      if (tokenRes.ok) {
        const tokenData = await tokenRes.json();
        const jwt = tokenData.id_token || tokenData.access_token;
        if (jwt) {
          onLogin(jwt);
          return;
        }
      }

      window.location.href = '/login';
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  if (loading && verificationSent) {
    return <CinematicLoader message="Verifying your account" submessage="Almost there..." />;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <div className="bg-brand-500 mx-auto flex h-16 w-16 items-center justify-center rounded-2xl text-2xl font-bold text-white shadow-lg">
            R
          </div>
          <h1 className="mt-4 text-3xl font-bold text-gray-900">
            {verificationSent ? 'Verify Your Email' : 'Create an Account'}
          </h1>
          <p className="mt-2 text-gray-500">
            {verificationSent ? 'Check your inbox for the verification code' : 'Start tailoring resumes with AI'}
          </p>
        </div>

        <div className="rounded-xl bg-white p-8 shadow-sm">
          {error && <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>}

          {!verificationSent ? (
            <>
              {COGNITO_AUTH_URL && (
                <>
                  <button
                    onClick={handleGoogleSignUp}
                    disabled={loading}
                    className="flex w-full items-center justify-center gap-3 rounded-lg border border-gray-300 bg-white px-4 py-3 font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50">
                    <svg className="h-5 w-5" viewBox="0 0 24 24">
                      <path
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                        fill="#4285F4"
                      />
                      <path
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        fill="#34A853"
                      />
                      <path
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        fill="#FBBC05"
                      />
                      <path
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        fill="#EA4335"
                      />
                    </svg>
                    Continue with Google
                  </button>

                  <div className="my-6 flex items-center">
                    <div className="flex-1 border-t" />
                    <span className="px-4 text-sm text-gray-400">or sign up with email</span>
                    <div className="flex-1 border-t" />
                  </div>
                </>
              )}

              <form onSubmit={handleSignUp} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                      First Name
                    </label>
                    <input
                      id="firstName"
                      type="text"
                      value={firstName}
                      onChange={e => setFirstName(e.target.value)}
                      autoComplete="given-name"
                      autoFocus
                      placeholder="John"
                      className="focus:border-brand-500 focus:ring-brand-500 mt-1 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-1"
                    />
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                      Last Name
                    </label>
                    <input
                      id="lastName"
                      type="text"
                      value={lastName}
                      onChange={e => setLastName(e.target.value)}
                      autoComplete="family-name"
                      placeholder="Doe"
                      className="focus:border-brand-500 focus:ring-brand-500 mt-1 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-1"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="reg-email" className="block text-sm font-medium text-gray-700">
                    Email Address
                  </label>
                  <input
                    id="reg-email"
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    autoComplete="email"
                    placeholder="you@example.com"
                    className="focus:border-brand-500 focus:ring-brand-500 mt-1 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-1"
                  />
                </div>

                <div>
                  <label htmlFor="reg-password" className="block text-sm font-medium text-gray-700">
                    Password
                  </label>
                  <div className="relative mt-1">
                    <input
                      id="reg-password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      autoComplete="new-password"
                      placeholder="Create a password"
                      className="focus:border-brand-500 focus:ring-brand-500 w-full rounded-lg border border-gray-300 px-4 py-2.5 pr-10 text-sm focus:outline-none focus:ring-1"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600">
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        {showPassword ? (
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                          />
                        ) : (
                          <>
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                            />
                          </>
                        )}
                      </svg>
                    </button>
                  </div>
                </div>

                <div>
                  <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700">
                    Confirm Password
                  </label>
                  <input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                    className="focus:border-brand-500 focus:ring-brand-500 mt-1 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-1"
                  />
                </div>

                {password && (
                  <div className="space-y-1.5">
                    <p className="text-xs font-medium text-gray-600">Password must:</p>
                    {requirementsList.map(({ key, label }) => (
                      <div key={key} className="flex items-center gap-2 text-xs">
                        {reqs[key] ? (
                          <svg className="h-4 w-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            />
                          </svg>
                        ) : (
                          <svg className="h-4 w-4 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                        <span className={reqs[key] ? 'font-medium text-green-600' : 'text-gray-500'}>{label}</span>
                      </div>
                    ))}
                  </div>
                )}

                {password && confirmPassword && password !== confirmPassword && (
                  <div className="rounded-lg bg-amber-50 p-3 text-sm text-amber-700">Passwords do not match</div>
                )}

                <button
                  type="submit"
                  disabled={
                    loading ||
                    !firstName ||
                    !email ||
                    !password ||
                    !confirmPassword ||
                    !isPasswordValid(reqs) ||
                    password !== confirmPassword
                  }
                  className="bg-brand-500 hover:bg-brand-600 w-full rounded-lg px-4 py-3 font-medium text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50">
                  {loading ? 'Creating account...' : 'Sign Up'}
                </button>
              </form>

              <div className="mt-4 text-center">
                <Link to="/login" className="text-brand-600 hover:text-brand-700 text-sm">
                  Already have an account? Sign In
                </Link>
              </div>
            </>
          ) : (
            <form onSubmit={handleVerify} className="space-y-4">
              <p className="text-sm text-gray-600">
                We've sent a verification code to <span className="font-medium">{email}</span>. Enter it below to verify
                your account.
              </p>
              <div>
                <label htmlFor="code" className="block text-sm font-medium text-gray-700">
                  Verification Code
                </label>
                <input
                  id="code"
                  type="text"
                  value={verificationCode}
                  onChange={e => setVerificationCode(e.target.value)}
                  autoFocus
                  placeholder="Enter 6-digit code"
                  className="focus:border-brand-500 focus:ring-brand-500 mt-1 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-center text-lg tracking-widest focus:outline-none focus:ring-1"
                />
              </div>
              <button
                type="submit"
                disabled={loading || !verificationCode.trim()}
                className="bg-brand-500 hover:bg-brand-600 w-full rounded-lg px-4 py-3 font-medium text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50">
                {loading ? 'Verifying...' : 'Verify & Sign In'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
